<template>
    <div class="konva-campaign-builder-container">
        <KeyboardShortcutsSidebar
            :class="{'fade-out': !showKeyboardShortcuts}"
            @close-sidebar="closeKeyboardShortcutsSidebar"
        />

        <div class="campaign-header-container" :class="{'vue-funnel-header-shrink': showKeyboardShortcuts}">
            <AutomationBuilderHeader
                v-if="campaignDataLoaded"
                :categories-loaded="categoriesLoaded"
                :funnel-status="funnelStatus"
                :all-categories="allCategories"
                :assigned-categories="assignedCategories"
                :beta-features-enabled="betaFeaturesEnabled"
                :builder-info="builderInfo"
                :flags-info="flagsInfo"
                :funnel-info="funnelInfo"
                :app-info="appInfo"
                :last-saved-timestamp="lastSavedTimestamp"
                :save-status="saveStatus"
                :show-reporting-view="showReportingView"
                :show-publish="showPublish"
                :funnel-draft-xml="draftXml"
                :draft-json="draftJson"
                :auto-open-config-screen="autoOpenConfigScreen"
                @save-and-close="saveAndClose"
                @close-automation-builder="closeAutomationBuilder"
                @update-categories="updateCategories"
                @save-version="saveVersion"
                @update-open-automatically-campaign="updateOpenAutomaticallyCampaign"
                @reporting-view-clicked="toggleReportingView"
                @update-name="updateFunnelName"
                @toggle-campaign-lock="toggleCampaignLock"
                @unpublish-campaign="unpublishCampaign"
                @save-campaign="saveCampaign"
                @show-publish-modal="onShowPublishModal"
                @verify-and-publish="verifyAndPublish"
                @open-cell-modal-by-id="openCellModalById"
                @import-xml="importXmlToBuilder"
                @restore-version="restoreVersion"
                @mark-dirty="markDirty"
                @revert-changes="revertChanges"
                @refresh-automation-categories="fetchCategories"
                @unlock-automation-status="unlockAutomationEditorStatus"
                @toggle-beta-features="toggleBetaFeatures"
            />
        </div>

        <KonvaCampaignCanvas
            v-if="flagsLoaded && campaignDataLoaded"
            data-qa="konva-campaign-canvas-wrapper"
            :show-reporting-view="showReportingView"
            :app-info="appInfo"
            :funnel-info="funnelInfo"
            :flags-info="flagsInfo"
            :role-info="roleInfo"
            :draft-json="draftJson"
            :published-json="publishedJson"
            :selected-sequence="konvaState.selectedSequenceId"
            :show-config-modal="showConfigModal"
            :selected-cells="konvaState.selectedCells"
            :is-spa-app="isSpaApp"
            :enabled-integrations="enabledIntegrations"
            :enabled-cell-types="enabledCellTypes"
            :beta-features-enabled="betaFeaturesEnabled"
            @open-element-list="() => elementListOpen = true"
            @replace-sequence="replaceSequence"
            @toggle-sequence-ready="toggleSequenceReady"
            @set-sequence-id="onSetSequenceId"
            @set-selected-cells="onSetSelectedCells"
            @update-cells="updateCells"
            @update-cells-global-state="updateCellsGlobalState"
            @update-cell-name="updateCellName"
            @add-cell="onAddCell"
            @add-cell-between="onAddCellBetween"
            @save-campaign="saveCampaign"
            @open-config-modal="cell => openCellModal(cell)"
            @toggle-keyboard-shortcuts-sidebar="toggleKeyboardShortcutsSidebar"
            @open-rename-modal="cell => openCellRename(cell)"
            @open-settings-modal="cell => openCellSettings(cell)"
            @open-automation-feedback-modal="openFeedbackModal"
            @open-new-email-experience="openNewEmailExperienceModal"
            @update-cell-value="updateCellValue"
        />

        <component
            :is="configModal"
            v-if="showConfigModal"
            data-qa="cell-config-modal"
            :enabled-integrations="enabledIntegrations"
            :is-open="showConfigModal"
            :cell="cellModal"
            :cells="draftJson && draftJson.cells"
            :app-info="appInfo"
            :flags-info="flagsInfo"
            :funnel-id="funnelInfo.funnelId"
            :funnel-name="funnelInfo.funnelName"
            :merge-fields="mergeFields"
            :convrrt-token-exists="convrrtTokenExists"
            :automatically-convert-bard="automaticallyConvertBard"
            :auto-open-content="autoOpenContentAfterMigration"
            @mark-dirty="markDirty"
            @set-convrrt-token-exists="setConvrrtTokenExists"
            @close-modal="closeModal"
            @update-cell-value="updateCellValue"
            @update-cell-name="updateCellName"
            @migrated-bard-to-unlayer="handleMigratedBardToUnlayer"
        />

        <AddContactsToAutomation
            v-if="showAddContactModal"
            :is-open="showAddContactModal"
            :funnel-id="funnelInfo.funnelId"
            :draft-xml="draftXml"
            @close-add-contact-to-automation="closeAddContactToAutomation"
            @set-add-contact-to-automation="setAddContactToAutomation"
            @update-dismiss-add-contacts="updateDismissAddContacts"
        />

        <KonvaSequenceSettingsModal
            v-if="showCellSettingsModal && cellSettingsModalData.type === $options.FLOW_TYPE"
            is-open
            :cell="cellSettingsModalData"
            @update-cell="updateCellValue"
            @close-cell-settings-modal="closeCellSettingsModal"
        />

        <KonvaCellSettingsModal
            v-if="showCellSettingsModal && cellSettingsModalData.type !== $options.FLOW_TYPE || showRenameCellModal"
            is-open
            :cell="cellSettingsModalData"
            :all-cells="draftJson && draftJson.cells"
            :app-info="appInfo"
            :show-settings="showCellSettingsModal"
            :show-rename="showRenameCellModal"
            @close-cell-settings-modal="closeCellSettingsModal"
            @update-cell-name="updateCellName"
        />

        <ShareFeedbackModal
            :v-if="isFeedbackModalOpen"
            :is-open="isFeedbackModalOpen"
            class="automation-builder-feedback-modal"
            :feature="$options.CAMPAIGN_BUILDER_FEEDBACK"
            :slack-channels="shareFeedbackModalSlackChannels"
            @close="isFeedbackModalOpen = false"
        />

        <AutomationElementListModal
            v-if="elementListOpen"
            :cells="draftJson && draftJson.cells"
            @close-element-list="elementListOpen = false"
            @set-sequence-id="onSetSequenceId"
            @open-config-modal="cell => openCellModal(cell)"
        />

        <PublishSuccessModal
            v-if="showPublishSuccess"
            :is-open="showPublishSuccess"
            @close="closePublishSuccessModal"
            @view-automations="closeAutomationBuilder"
        />

        <DsConfirmDialog
            v-if="showTimeZoneChangedModal"
            :is-open="showTimeZoneChangedModal"
            :title="$t('funnel.edit.timeZone.changed.title')"
            :confirm-label="$t('funnel.edit.timeZone.changed..gotit')"
            confirm-only
            size="med"
            @cancel="closeTimeZoneChangedModal"
            @confirm="closeTimeZoneChangedModal"
        >
            <template #message>
                <DsInlineAlert leading-icon type="info" class="timezone-info">
                    {{ $t('funnel.edit.timeZone.changed.details.header') }}
                </DsInlineAlert>
                <div class="timezone-desc">
                    {{ $t('funnel.edit.timeZone.changed.details.description.1') }}
                    <strong>{{ oldTimeZoneLabel }}</strong>
                    {{ $t('funnel.edit.timeZone.changed.details.description.1.to') }}
                    <strong>{{ newTimeZoneLabel }}</strong>.
                    {{ $t('funnel.edit.timeZone.changed.details.2') }}
                    {{ $t('funnel.edit.timeZone.changed.details.3') }}<br />
                </div>
                <div>
                    <strong>{{ $t('funnel.edit.timeZone.changed.example.header') }}</strong><br />
                    <strong>{{ oldDateTime }}</strong>
                    <strong>{{ oldTimeZoneLabel }}</strong>
                    {{ $t('funnel.edit.timeZone.changed.example.isNow') }}
                    <strong>{{ newDateTime }}</strong>
                    <strong>{{ newTimeZoneLabel }}</strong>.
                </div>
            </template>
        </DsConfirmDialog>

        <link
            rel="preload"
            as="font"
            href="/resources/vue/fonts/Poppins-Bold.otf"
            type="font/otf"
            crossorigin
        />
    </div>
</template>

<script>
// External imports
import Vue from 'vue';
import axios from 'axios';
import sentry from '@/analytics/sentry';
import fullstory from '@/analytics/fullstory';
import clonedeep from 'lodash.clonedeep';

// API calls
import { getConfig } from './configuration/itemScreen/ItemScreenDisplayModal.api';
import { getAllCategories, getAllCategoriesAssigned } from './configuration/editNameAndCategory/edit.name.and.category.api';
import {
    unlockEditor,
    fetchDraftXml,
    fetchPublishedXml,
    getEnabledCellTypes,
    getEnabledIntegrations,
    disableBetaFeatures,
    revertXml,
} from './campaign.api';
import { unpublish } from './configuration/unpublish/unpublish.api';
import { restoreVersion } from './configuration/restoreVersion/restore.version.api';
import { updateLockForFunnel } from './configuration/locking/lock.api';

import { clearPotentialContactsList } from './configuration/addContactsToAutomation/add.contacts.to.automation.api';
import { getMergeFieldsDropdown } from './utils/mergeFieldsDropdown.api';
import {
    getLabelsFromTimeZoneChanged,
} from './utils/time-zone-changed-api';

// Utils
import {
    generateEmptyCampaignCells,
    mapCampaignJsonToXml,
    mapCampaignXmlToJson,
    transformAndSaveCampaign,
    revertAdditionalObjectsIfNeeded,
} from './utils/cell/automation-mapper-utils';

import {
    getLastCellInSequence,
    isEdgeValid,
    findIncomingCells,
    findOutgoingCells,
    findOutgoingEdges,
    hasNewEdges,
    hasRemovedEdges,
} from './utils/cell/automation-edge-utils';

import {
    findChildren,
    checkBrokenCells,
    getCellById,
    hasNewOrModifiedVertexes,
    hasRemovedVertexes,
} from './utils/cell/automation-cell-utils';
import { copyCell } from './utils/konva-copy-utils';
import { handleDecisionDiamondCreationAndUpdate, sanitizeDecisionBeforePublish } from './decisionDiamonds/decision-utils';

// constants
import {
    GOALS,
    PROCESSES,
    TIMERS,
    COMMUNICATIONS,
    DECISION_MODAL,
    EDGE_VALIDATION_STATE,
    STYLE_BY_META_TYPE,
    FLOW_TYPE,
    EDGE_TYPE,
    MAIN_CANVAS_ID,
    SEQUENCE_START,
    EMAIL_CONFIRMATION_LINK_CLICK_NAME,
    EMAIL_CONFIRMATION_DELAY_TIMER_NAME,
    EMAIL_CONFIRMATION_DEFAULT_LINK_CLICK,
    EMAIL_CONFIRMATION_DEFAULT_DELAY_TIMER,
    DECISION_TYPE,
    NOTES_TYPE,
    GRID_SIZE,
    STANDARD_CELL_SIZE,
    AUTOMATION_STATUS,
    GOAL_ACHIEVEMENT_TYPE,
    INTEGRATION_TRIGGER_TYPE,
    INTEGRATION_ACTION_TYPE,
    FLOW_TYPE_SETTING,
    SAVE_STATUS,
} from './utils/editor.constants';
import { syncLabel } from './utils/label.sync';
import { SLACK_CHANNELS } from '@/components/util/slack/slack.constants';
import { CAMPAIGN_BUILDER as CAMPAIGN_BUILDER_FEEDBACK } from '@/components/shared/constants/shareFeedback.constants';

const AUTOSAVE_DELAY_TIME_MS = 15 * 1000; // 15 seconds
const URL_VIEW = { PERFORMANCE: 'performance' };

// config screens
import {
    ProcessModals,
    GoalModals,
    TimerModals,
    CommunicationsModals,
    OtherModals,
} from './elements';

// components
import KonvaCampaignCanvas from './editor/KonvaCampaignCanvas.vue';
import KonvaCellSettingsModal from './editor/KonvaCellSettingsModal.vue';
import KonvaSequenceSettingsModal from './editor/KonvaSequenceSettingsModal.vue';
import AutomationBuilderHeader from './header/AutomationBuilderHeader.vue';
import KeyboardShortcutsSidebar from './editor/KeyboardShortcutsSidebar.vue';
import AddContactsToAutomation from './configuration/addContactsToAutomation/AddContactsToAutomation.vue';
import ShareFeedbackModal from '@/components/shared/ShareFeedbackModal.vue';
import AutomationElementListModal from './configuration/automationElementListModal/AutomationElementListModal.vue';
import PublishSuccessModal from './configuration/publish/PublishSuccessModal.vue';
import {
    maxCellIdStore,
} from './utils/max-cell-id-utils';

export default {
    FLOW_TYPE,
    CAMPAIGN_BUILDER_FEEDBACK,
    AUTOSAVE_DELAY_TIME_MS,
    FLOW_TYPE_SETTING,
    EMAIL_CONFIRMATION_LINK_CLICK_NAME,
    EMAIL_CONFIRMATION_DELAY_TIMER_NAME,
    EMAIL_CONFIRMATION_DEFAULT_LINK_CLICK,
    EMAIL_CONFIRMATION_DEFAULT_DELAY_TIMER,
    components: {
        KonvaCampaignCanvas,
        AutomationBuilderHeader,
        KeyboardShortcutsSidebar,
        AddContactsToAutomation,
        KonvaCellSettingsModal,
        KonvaSequenceSettingsModal,
        ShareFeedbackModal,
        AutomationElementListModal,
        PublishSuccessModal,
    },


    props: {
        builderProperties: {
            type: String,
            required: true,
        },
        appProperties: {
            type: String,
            required: true,
        },
        userPermissions: {
            type: String,
            required: true,
        },
        funnelProperties: {
            type: String,
            required: true,
        },
        flagsProperties: {
            type: String,
            required: true,
        },
        pluginSettings: {
            type: String,
            required: true,
        },
        version: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            builderInfo: {},
            appInfo: {},
            roleInfo: {},
            funnelInfo: {
                funnelName: '',
                publishedXml: '',
            },
            draftJson: null,
            publishedJson: null,
            assignedCategories: [],
            allCategories: [],
            konvaState: {
                isDirty: false,
                selectedSequenceId: '1',
                selectedCells: [],
            },
            categoriesLoaded: false,
            flagsLoaded: false,
            campaignDataLoaded: false,
            convrrtTokenExists: false,
            showPublish: false,
            showPublishSuccess: false,
            draftXml: '',
            flagsInfo: {},
            pluginConfig: {},
            startDate: null,
            endDate: null,
            uniqueContactsMap: null,
            showConfigModal: false,
            showReportingView: false,
            autoOpenConfigScreen: false,
            lastSavedTimestamp: 0,
            saveStatus: SAVE_STATUS.NONE,
            cellModal: null,
            configModal: null,
            showAddContactModal: false,
            addContactToAutomation: false,
            dismissAddContactsModal: false,
            showKeyboardShortcuts: false,
            mergeFields: [],
            loadingMergeFields: true,
            showCellSettingsModal: false,
            showRenameCellModal: false,
            cellSettingsModalData: null,
            automaticallyConvertBard: false,
            showSequenceSettingsModal: false,
            showTimeZoneChangedModal: false,
            enabledCellTypes: [],
            brokenCellsById: {},
            enabledIntegrations: [],
            modalComponents: {
                [GOALS.GOAL_API]: GoalModals.ApiModal,
                [GOALS.GOAL_SMART_FORM]: GoalModals.FormSubmitted,
                [GOALS.GOAL_QUOTE_STATUS]: GoalModals.QuoteStatus,
                [GOALS.GOAL_TAG_APPLIED]: GoalModals.TagApplied,
                [GOALS.GOAL_UNLAYER_LANDING_PAGE]: GoalModals.LandingPage,
                [GOALS.GOAL_LANDING_PAGE_SUBMITTED]: GoalModals.LandingPageSubmitted,
                [GOALS.GOAL_PRODUCT_PURCHASED]: GoalModals.ProductPurchased,
                [GOALS.GOAL_LEAD_SCORE_ACHIEVED]: GoalModals.LeadScore,
                [GOALS.GOAL_WEB_PAGE_AUTOMATION]: GoalModals.WebPageAutomation,
                [GOALS.GOAL_WORDPRESS_OPTIN]: GoalModals.WordPressOptIn,
                [GOALS.GOAL_FAILED_PURCHASE]: GoalModals.FailedPurchase,
                [GOALS.GOAL_OPPORTUNITY_STAGE_MOVED]: GoalModals.OpportunityStageMoved,
                [GOALS.GOAL_APPOINTMENT_EVENT]: GoalModals.AppointmentEvent,
                [GOALS.GOAL_EMAIL_LINK_CLICKED]: GoalModals.EmailLinkClicked,
                [GOALS.GOAL_PIPELINE_STAGE_MOVED]: GoalModals.PipelineStageMove,
                [GOALS.GOAL_TASK_COMPLETED]: GoalModals.TaskComplete,
                [GOALS.GOAL_CONVRRT_PAGE_BUILDER]: GoalModals.ConvrrtPageBuilder,
                [GOALS.GOAL_NOTE_APPLIED]: GoalModals.NoteApplied,
                [GOALS.GOAL_WEB_FORM]: GoalModals.WebFormSubmitted,
                [GOALS.GOAL_INTERNAL_FORM]: GoalModals.InternalFormSubmitted,
                [PROCESSES.PROCESS_APPLY_REMOVE_TAG]: ProcessModals.ApplyRemoveTag,
                [PROCESSES.PROCESS_SEND_HTTP_POST]: ProcessModals.SendHttpPost,
                [PROCESSES.PROCESS_SEND_HTTP_REQUEST]: ProcessModals.SendHttpRequest,
                [PROCESSES.PROCESS_ASSIGN_OWNER]: ProcessModals.AssignOwner,
                [PROCESSES.PROCESS_CANCEL_SUBSCRIPTION]: ProcessModals.CancelSubscription,
                [PROCESSES.PROCESS_CREATE_APPOINTMENT]: ProcessModals.CreateAppointment,
                [PROCESSES.PROCESS_CREATE_OPPORTUNITY]: ProcessModals.CreateOpportunity,
                [PROCESSES.PROCESS_UNLAYER_EMAIL]: CommunicationsModals.UnlayerEmail,
                [PROCESSES.PROCESS_BARD_EMAIL]: CommunicationsModals.UnsupportedBardEmail,
                [PROCESSES.PROCESS_CREATE_TASK]: ProcessModals.CreateTask,
                [PROCESSES.PROCESS_AUTOMATED_SMS]: CommunicationsModals.SmsBuilder,
                [PROCESSES.PROCESS_CREATE_ORDER]: ProcessModals.CreateOrder,
                [PROCESSES.PROCESS_APPLY_NOTE]: ProcessModals.ApplyNote,
                [PROCESSES.PROCESS_CREATE_DEAL]: ProcessModals.CreateDeal,
                [PROCESSES.PROCESS_SET_FIELD_VALUE]: ProcessModals.SetFieldValue,
                [PROCESSES.PROCESS_ADD_TO_SEQUENCE]: ProcessModals.AddToSequence,
                [PROCESSES.PROCESS_ACTION_SET]: ProcessModals.ActionSet,
                [PROCESSES.PROCESS_MOVE_OPPORTUNITY]: ProcessModals.MoveOpportunity,
                [PROCESSES.PROCESS_FULFILLMENT_LIST]: ProcessModals.FulfillmentModal,
                [PROCESSES.PROCESS_LETTER]: CommunicationsModals.LetterModal,
                [TIMERS.TIMER_FIELD_CONTACT]: TimerModals.FieldTimer,
                [TIMERS.TIMER_APPOINTMENT]: TimerModals.AppointmentTimer,
                [TIMERS.TIMER_DATE]: TimerModals.DateTimer,
                [TIMERS.TIMER_DELAY]: TimerModals.DelayTimer,
                [COMMUNICATIONS.PROCESS_LEGACY_EMAIL]: CommunicationsModals.LegacyEmail,
                [COMMUNICATIONS.PROCESS_EMAIL_CONFIRM]: CommunicationsModals.EmailConfirmation,
                [DECISION_MODAL.DECISION_DIAMOND]: OtherModals.DecisionDiamond,
                [INTEGRATION_TRIGGER_TYPE]: OtherModals.IntegrationTriggerModal,
                [INTEGRATION_ACTION_TYPE]: OtherModals.IntegrationActionModal,
            },
            shareFeedbackModalSlackChannels: [SLACK_CHANNELS.IN_APP_FEEDBACK],
            isFeedbackModalOpen: false,
            elementListOpen: false,
            oldTimeZoneLabel: '',
            newTimeZoneLabel: '',
            oldDateTime: '',
            newDateTime: '',
            automationMessageId: null,
            autoOpenContentAfterMigration: false,
        };
    },

    computed: {
        funnelStatus() {
            if (!this.funnelInfo.published) return AUTOMATION_STATUS.DRAFT;

            if (this.hasChangesToBePublished) return AUTOMATION_STATUS.PUBLISHED_WITH_CHANGES;

            return AUTOMATION_STATUS.PUBLISHED;
        },

        fullstoryEnabled() {
            return this.flagsInfo.fullstoryEnabled;
        },

        isDirty() {
            return this.konvaState.isDirty;
        },

        isSpaApp() {
            return this.appInfo?.appEditionFlags?.spaApp;
        },

        isUltimateNavigationEnabled() {
            return this.flagsInfo.ultimateNavigationEnabled;
        },

        betaFeaturesEnabled() {
            return this.appInfo?.betaFeaturesEnabled ?? true;
        },

        hasChangesToBePublished() {
            return this.hasNewOrModifiedVertexes || this.hasNewEdges || this.hasRemovedVertexes || this.hasRemovedEdges;
        },

        hasNewEdges() {
            return hasNewEdges(this.draftJson?.cells, this.publishedJson?.cells);
        },

        hasRemovedEdges() {
            return hasRemovedEdges(this.draftJson?.cells, this.publishedJson?.cells);
        },

        hasNewOrModifiedVertexes() {
            return hasNewOrModifiedVertexes(this.draftJson?.cells, this.publishedJson?.cells);
        },

        hasRemovedVertexes() {
            return hasRemovedVertexes(this.draftJson?.cells, this.publishedJson?.cells);
        },
    },

    async mounted() {
        this.transform();
        this.showReportingView = new URLSearchParams(window.location.search).get('view') === URL_VIEW.PERFORMANCE;

        await this.fetchAndConvertFunnelXML();
        this.initializeAutosave();
        this.fetchCategories();
        this.fetchMergeFields();
        this.fetchItemDisplayConfiguration();
        this.getEnabledCellTypes();

        sentry.init(Vue);

        if (this.fullstoryEnabled) {
            fullstory.init(this.appInfo);
        }

        if (this.funnelInfo.locked) {
            this.showReportingView = true;
        }

        this.checkForTimeZones();

        await this.fetchAutomationMessageId();

        if (maxCellIdStore.maxCellId === 0) {
            this.initializeMaxCellId();
        }

        window.addEventListener('beforeunload', () => {
            this.saveAndUnlock();
        });
    },

    unmounted() {
        window.removeEventListener('beforeunload');
    },

    methods: {
        saveAndUnlock() {
            try {
                if (this.isDirty) this.saveCampaign();
            } catch (error) {
                // swollow error to make sure unlock is called
            }
            this.unlockAutomationEditorStatus();
        },

        onSetSelectedCells(selectedCells) {
            const draftSelectedCells = selectedCells.map((selectedCell) => this.draftJson?.cells.find((cell) => cell.id === selectedCell.id));

            const cellsInCurrentSequence = draftSelectedCells.filter((cell) => cell.meta.parent === this.konvaState.selectedSequenceId);

            this.konvaState.selectedCells = cellsInCurrentSequence;
        },

        onSetSequenceId(sequenceId) {
            this.onSetSelectedCells([]);
            this.konvaState.selectedSequenceId = `${sequenceId}`;
        },

        getCellData(name, type, position, parent, autoCreateSequence) {
            return {
                cellName: type,
                cellDefaultName: name,
                cellDefaultType: type,
                parent,
                x: position.x,
                y: position.y,
                autoCreateSequence,
                autoCreateEdge: false,
                autoOpenSequence: false,
                ignoreAutoOpen: true,
            };
        },

        async onAddCellBetween(data) {
            const {
                cellName,
                cellDefaultType,
                sourceElement,
                isSequenceView,
                isBigCard,
            } = data;
            const position = {
                x: sourceElement.mxGeometry.x + (GRID_SIZE * (isBigCard ? 5 : 2.5)),
                y: sourceElement.mxGeometry.y,
            };
            const autoCreateSequence = (Object.values(PROCESSES).includes(cellDefaultType) || Object.values(TIMERS).includes(cellDefaultType) || SEQUENCE_START === cellDefaultType) && !isSequenceView;
            const outgoingEdges = [...findOutgoingEdges(sourceElement.id, this.draftJson?.cells)];
            const cellPayload = this.getCellData(cellName, cellDefaultType, position, sourceElement.meta.parent, autoCreateSequence);

            const newCell = await this.onAddCell(cellPayload);

            const cellElement = autoCreateSequence
                ? getCellById(newCell?.meta.parent, this.draftJson?.cells)
                : newCell;

            let edgeId = cellElement.id;

            if (cellDefaultType === PROCESSES.PROCESS_EMAIL_CONFIRM) {
                const lockedCells = findOutgoingCells(cellElement.id, this.draftJson?.cells);

                if (lockedCells.length > 0) {
                    const lockedCell = lockedCells[0];

                    edgeId = lockedCell.id;
                }
            }

            for (const edge of outgoingEdges) {
                if (isEdgeValid(`${edgeId}`, edge.meta.target, this.draftJson?.cells)) {
                    edge.meta.source = `${edgeId}`;
                }
            }

            if (isEdgeValid(`${sourceElement.id}`, `${cellElement.id}`, this.draftJson?.cells)) {
                await this.onAddCell({
                    cellName: EDGE_TYPE,
                    target: `${cellElement.id}`,
                    source: `${sourceElement.id}`,
                    parent: `${cellElement.meta.parent}`,
                    autoSelectCell: false,
                });
            }
        },

        async onAddCell(event) {
            const {
                autoCreateSequence,
                autoOpenSequence = true,
                autoCreateEdge,
                autoSelectCell = true,
                ignoreAutoOpen = false,
                addToSelection,
                edgeSource,
            } = event;

            let newCell;
            let connectedElement;
            let newSequence;

            if (autoCreateSequence) {
                const { value, ...sanitizedEvent } = event;

                const newSequenceElement = {
                    ...sanitizedEvent,
                    parent: MAIN_CANVAS_ID,
                    cellDefaultName: this.$t('funnel.edit.sideBar.template.flow'),
                    cellName: FLOW_TYPE,
                };

                if (event.cellName === COMMUNICATIONS.PROCESS_EMAIL_CONFIRM) {
                    newSequenceElement.cellDefaultName = this.$t('funnel.edit.emailConfirm.sequence.default.name');
                    newSequenceElement.ready = '0';
                    newSequenceElement.methodLocked = '1';
                    newSequenceElement.flowItemsLocked = '1';
                }

                newSequence = await this.addCellToDraft(newSequenceElement);

                if (autoOpenSequence) this.onSetSequenceId(newSequence.meta.id);

                const startCell = await this.addCellToDraft({
                    cellName: SEQUENCE_START,
                    cellDefaultName: this.$t('funnel.edit.sideBar.flow.start'),
                    parent: `${newSequence.meta.id}`,
                    x: GRID_SIZE,
                    y: GRID_SIZE,
                });

                connectedElement = newSequence;

                if (event.cellName !== SEQUENCE_START) {
                    const newCellElement = {
                        ...event,
                        cellDefaultName: event.cellDefaultName,
                        parent: `${newSequence.meta.id}`,
                        x: GRID_SIZE * 6,
                        y: GRID_SIZE,
                    };

                    if (event.cellName === COMMUNICATIONS.PROCESS_EMAIL_CONFIRM) {
                        newCellElement.initialized = '0';
                    }

                    newCell = await this.addCellToDraft(newCellElement);

                    await this.addCellToDraft({
                        cellName: EDGE_TYPE,
                        parent: `${newSequence.meta.id}`,
                        source: startCell.id,
                        target: newCell.id,
                    });

                    if (event.cellName === COMMUNICATIONS.PROCESS_EMAIL_CONFIRM) {
                        await this.addEmailConfirmationElements(newSequence, newCell);
                    }
                } else {
                    newCell = startCell;
                }
            } else {
                newCell = await this.addCellToDraft(event);
                connectedElement = newCell;
            }

            if (autoCreateEdge) {
                if (event.cellName === EDGE_TYPE) return newCell;

                const lastElement = edgeSource || newSequence || getLastCellInSequence(event.parent, this.draftJson?.cells);

                if (!lastElement) return newCell;

                const canAutoCreateEdge = isEdgeValid(lastElement.meta.id, connectedElement.meta.id, [...(this.draftJson?.cells || []), connectedElement]) === EDGE_VALIDATION_STATE.VALID;

                if (canAutoCreateEdge) {
                    await this.addCellToDraft({
                        cellName: EDGE_TYPE,
                        parent: event.parent,
                        source: lastElement.meta.id,
                        target: connectedElement.meta.id,
                    });
                }
            }

            if (event.parent === MAIN_CANVAS_ID && newCell && newCell.type === EDGE_TYPE) {
                await this.updateCellsGlobalState([newCell.meta.target]);
            }

            if (this.autoOpenConfigScreen && newCell) {
                const skipAutoOpen = [EDGE_TYPE, NOTES_TYPE, SEQUENCE_START].includes(newCell.type);

                if (!skipAutoOpen && !ignoreAutoOpen) this.openCellModal(newCell);
            }

            if (newCell && autoSelectCell) {
                if (addToSelection) {
                    this.onSetSelectedCells([...this.konvaState.selectedCells, newCell]);
                } else {
                    this.onSetSelectedCells([newCell]);
                }
            }

            return newCell;
        },

        async updateCellsGlobalState(cellIds) {
            const promiseList = [];

            for (const cellId of cellIds) {
                const cellElement = getCellById(cellId, this.draftJson?.cells);

                if (cellElement && cellElement.type !== FLOW_TYPE && cellElement.cellName !== DECISION_TYPE) {
                    const incomingCells = findIncomingCells(cellId, this.draftJson?.cells);
                    const isReady = cellElement.value.ready === '1';

                    cellElement.value.global = incomingCells.some((item) => item.type === FLOW_TYPE)
                        ? GOAL_ACHIEVEMENT_TYPE.NOT_GLOBAL
                        : GOAL_ACHIEVEMENT_TYPE.GLOBAL;

                    promiseList.push(this.updateCellValue({ cell: cellElement, isReady }));
                }
            }
            await Promise.all(promiseList);
        },

        async addCellToDraft(event) {
            const {
                cellName, parent, x, y, cellDefaultName, source, target, value, methodLocked, flowItemsLocked, ready, initialized, metaType,
            } = event;

            maxCellIdStore.increment();

            const newCellId = `${maxCellIdStore.maxCellId}`;
            let { width, height } = event;

            if (!width || !height) {
                if (cellName === FLOW_TYPE) {
                    width = 128;
                    height = 64;
                } else {
                    width = STANDARD_CELL_SIZE;
                    height = STANDARD_CELL_SIZE;
                }
            }

            const xPos = x && Math.floor(x / GRID_SIZE) * GRID_SIZE;
            const yPos = y && Math.floor(y / GRID_SIZE) * GRID_SIZE;

            const readyState = ([SEQUENCE_START, FLOW_TYPE].includes(cellName)) ? '1' : '0';
            const newCell = {
                id: newCellId,
                type: cellName,
                meta: {
                    id: newCellId,
                    parent,
                    style: cellName,
                },
            };

            newCell.meta.style = STYLE_BY_META_TYPE[cellName] || cellName;

            if (cellName === EDGE_TYPE) {
                newCell.mxGeometry = {
                    as: 'geometry',
                    relative: '1',
                };
            } else {
                newCell.mxGeometry = {
                    as: 'geometry',
                    height,
                    width,
                    x: xPos,
                    y: yPos,
                };
                newCell.value = {
                    ...value,
                    as: 'value',
                    initialized: initialized || '0',
                    name: cellDefaultName,
                    published: '0',
                    broken: '0',
                    ready: ready || readyState,
                };

                if (parent === MAIN_CANVAS_ID) {
                    if (Object.values(GOALS).includes(cellName) || cellName === INTEGRATION_TRIGGER_TYPE) {
                        newCell.meta.metaType = metaType || cellName;
                        newCell.value.global = GOAL_ACHIEVEMENT_TYPE.GLOBAL;
                    }
                }

                newCell.text = cellDefaultName;
                newCell.meta.vertex = '1';
            }

            if (cellName === GOALS.GOAL_EMAIL_CONFIRM) {
                newCell.value.global = GOAL_ACHIEVEMENT_TYPE.NOT_GLOBAL;
            }

            if (cellName === FLOW_TYPE) {
                newCell.value.flowType = FLOW_TYPE_SETTING.STOP;

                if (methodLocked) newCell.value.methodLocked = methodLocked;
                if (flowItemsLocked) newCell.value.flowItemsLocked = flowItemsLocked;
            }

            if (cellName === EDGE_TYPE) {
                newCell.meta.edge = '1';
                newCell.meta.source = source;
                newCell.meta.target = target;

                const { cellMetaSource, ddDraftJson } = await handleDecisionDiamondCreationAndUpdate({
                    newCell,
                    source,
                    target,
                    draftJson: this.draftJson,
                    konvaState: this.konvaState,
                });

                this.draftJson = ddDraftJson;
                newCell.meta.source = cellMetaSource;
            }

            if (cellName === NOTES_TYPE) {
                newCell.value.notes = newCell.value.notes ? newCell.value.notes.trim() : '';
                newCell.mxGeometry.x = x;
                newCell.mxGeometry.y = y;
            }

            this.draftJson.cells = [...(this.draftJson?.cells || []), newCell];

            this.markDirty();

            return newCell;
        },

        async saveAndClose() {
            if (this.isDirty) await this.saveCampaign();

            await this.closeAutomationBuilder();
        },

        async closeAutomationBuilder() {
            const { spaBaseUrl, spaAppParameter } = this.appInfo;
            const advancedAutomationRoute = 'my-automations/list/advanced';

            await this.unlockAutomationEditorStatus();

            if (this.isSpaApp || this.isUltimateNavigationEnabled) {
                window.location.href = `${spaBaseUrl}/${advancedAutomationRoute}?${spaAppParameter}`;
            } else {
                window.location.href = '/Reports/searchTemplate.jsp';
            }
        },

        async unlockAutomationEditorStatus() {
            await unlockEditor(this.funnelInfo.funnelId, this.funnelInfo.hash);
        },

        async addEmailConfirmationElements(startSequence, newCell) {
            const newDelayTimer = await this.addCellToDraft({
                cellName: TIMERS.TIMER_DELAY,
                parent: `${startSequence.meta.id}`,
                cellDefaultName: EMAIL_CONFIRMATION_DELAY_TIMER_NAME,
                value: EMAIL_CONFIRMATION_DEFAULT_DELAY_TIMER,
                ready: '1',
                x: GRID_SIZE * 11,
                y: GRID_SIZE,
            });

            await this.addCellToDraft({
                cellName: EDGE_TYPE,
                parent: `${startSequence.meta.id}`,
                source: newCell.id,
                target: newDelayTimer.id,
            });

            const newEmailClick = await this.addCellToDraft({
                cellName: GOALS.GOAL_EMAIL_CONFIRM,
                metaType: GOALS.GOAL_EMAIL_LINK_CLICKED,
                parent: MAIN_CANVAS_ID,
                cellDefaultName: EMAIL_CONFIRMATION_LINK_CLICK_NAME,
                value: EMAIL_CONFIRMATION_DEFAULT_LINK_CLICK,
                x: startSequence.mxGeometry.x + (GRID_SIZE * 5),
                y: startSequence.mxGeometry.y,
            });

            await this.addCellToDraft({
                cellName: EDGE_TYPE,
                parent: MAIN_CANVAS_ID,
                source: startSequence.id,
                target: newEmailClick.id,
            });
        },

        async saveCampaign() {
            const draftJsonSnapshot = JSON.stringify(this.draftJson);

            this.draftXml = await transformAndSaveCampaign({
                funnelId: this.funnelInfo.funnelId,
                draftJson: this.draftJson,
                maxCellId: maxCellIdStore.maxCellId,
                hash: this.funnelInfo.hash,
                appInfo: this.appInfo,
            });


            this.konvaState.isDirty = draftJsonSnapshot !== JSON.stringify(this.draftJson);

            return this.draftXml;
        },

        async saveVersion() {
            try {
                const xml = await this.saveCampaign();

                await axios.put(`/app/funnel/${this.funnelInfo.funnelId}/revision`, xml);

                this.$toast({ message: this.$t('funnel.edit.new.version') });
            } catch (err) {
                this.$toast({ message: this.$t('funnel.edit.revision.failure') });
            }
        },

        initializeMaxCellId () {
            const allCellIds = this.draftJson?.cells.map((cell) => {
                return parseInt(cell.meta.id, 10);
            }) || [];

            maxCellIdStore.setCount(Math.max(Math.max(...allCellIds, 1, maxCellIdStore.maxCellId)));
        },

        toggleSequenceReady(sequenceCell) {
            const isReady = sequenceCell.value.ready === '1';

            this.updateCellValue({ cell: sequenceCell, isReady: !isReady });
        },

        updateCells(updatedCells) {
            this.draftJson.cells = updatedCells;
            this.markDirty();
        },

        async fetchAndConvertFunnelXML() {
            const [{ data: campaignXml }, { data: publishedXml }] = await Promise.all([
                fetchDraftXml(this.funnelInfo.funnelId),
                fetchPublishedXml(this.funnelInfo.funnelId),
            ]);

            this.draftXml = campaignXml;
            this.funnelInfo.publishedXml = publishedXml;

            const [
                draftJson,
                publishedJson,
            ] = await Promise.all([
                mapCampaignXmlToJson(campaignXml, this.flagsInfo),
                mapCampaignXmlToJson(publishedXml, this.flagsInfo),
            ]);

            this.draftJson = draftJson;
            this.publishedJson = publishedJson;

            await this.checkForBrokenCells();

            if (draftJson && draftJson.cells && !draftJson.cells.length) {
                this.draftJson.cells = generateEmptyCampaignCells(this.funnelInfo.funnelId, this.appInfo);

                await this.saveCampaign();
            }
            this.campaignDataLoaded = true;
        },

        markDirty() {
            this.konvaState.isDirty = true;
        },

        setConvrrtTokenExists() {
            this.convrrtTokenExists = true;
        },

        // mxgraph functions

        initializeAutosave() {
            setInterval(async() => {
                if (this.isDirty && this.saveStatus !== SAVE_STATUS.SAVING) {
                    this.saveStatus = SAVE_STATUS.SAVING;

                    try {
                        await this.saveCampaign();
                        this.lastSavedTimestamp = Date.now();
                        this.saveStatus = SAVE_STATUS.SUCCESS;
                    } catch (err) {
                        this.saveStatus = SAVE_STATUS.FAILED;
                        // eslint-disable-next-line no-console
                        console.error('Error:', err);
                    }
                }
            }, AUTOSAVE_DELAY_TIME_MS);
        },

        transform() {
            this.builderInfo = JSON.parse(this.builderProperties);
            this.appInfo = JSON.parse(this.appProperties);
            this.roleInfo = JSON.parse(this.userPermissions);
            this.funnelInfo = JSON.parse(this.funnelProperties.replaceAll('&#39;', "'"));
            maxCellIdStore.setCount(this.funnelInfo.maxCellId);
            this.flagsInfo = JSON.parse(this.flagsProperties);
            this.pluginConfig = JSON.parse(this.pluginSettings);
            this.flagsLoaded = true;
        },

        toggleReportingView() {
            this.showReportingView = !this.showReportingView;
        },

        closeKeyboardShortcutsSidebar() {
            this.showKeyboardShortcuts = false;
        },

        toggleKeyboardShortcutsSidebar() {
            this.showKeyboardShortcuts = !this.showKeyboardShortcuts;
        },

        openCellRename(cell) {
            this.cellSettingsModalData = cell;

            this.showRenameCellModal = true;
        },

        openCellSettings(cell) {
            this.cellSettingsModalData = cell;

            if (cell.type !== FLOW_TYPE) this.showRenameCellModal = true;

            this.showCellSettingsModal = true;
        },

        closeCellSettingsModal() {
            this.cellSettingsModalData = null;
            this.showRenameCellModal = false;
            this.showCellSettingsModal = false;
        },

        openCellModal(cell) {
            this.cellModal = cell;
            const metaType = (cell.meta.metaType ?? cell.meta.style);

            if ([SEQUENCE_START, NOTES_TYPE].includes(metaType)) return;

            if (metaType === PROCESSES.PROCESS_BARD_EMAIL) {
                this.automaticallyConvertBard = true;
            }

            try {
                this.configModal = this.modalComponents[metaType] || OtherModals.ElementNotSupportedModal;

                if (metaType === INTEGRATION_TRIGGER_TYPE || metaType === INTEGRATION_ACTION_TYPE) {
                    const comparisonKey = metaType === INTEGRATION_TRIGGER_TYPE ? 'integrationTriggerId' : 'integrationActionId';
                    const enabledIntegration = this.enabledIntegrations.find((integration) => {
                        return integration.id === this.cellModal.value[comparisonKey];
                    });

                    if (!enabledIntegration) this.configModal = OtherModals.ElementNotSupportedModal;
                }

                this.showConfigModal = true;
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Error:', error);
            }
        },

        closeModal() {
            this.showConfigModal = false;
            this.configModal = '';
            this.cellModal = null;
            this.automaticallyConvertBard = false;
            this.autoOpenContentAfterMigration = false;
        },

        updateFunnelName(newFunnelName) {
            this.funnelInfo.funnelName = newFunnelName;
        },

        async replaceSequence({ otherJson, otherSequenceId, selectedSequence }) {
            const currentSequenceChildren = findChildren(selectedSequence, this.draftJson?.cells);
            const cellIdsToDelete = [];

            currentSequenceChildren.forEach((cell) => cellIdsToDelete.push(cell.id));

            const updatedCells = this.draftJson?.cells.filter((cell) => !cellIdsToDelete.includes(cell.id));

            this.draftJson = { ...this.draftJson, cells: updatedCells };

            const otherSequenceChildren = findChildren(otherSequenceId, otherJson?.cells);

            const oldCellNewCellMapping = [];

            const otherSequencesVertex = otherSequenceChildren.filter((child) => child.type !== EDGE_TYPE);

            await Promise.all(otherSequencesVertex.map(async (cell) => {
                const copiedCell = await copyCell(cell, null, this.funnelInfo.funnelId);
                const newCell = await this.addCellToDraft({
                    cellName: cell.type,
                    parent: selectedSequence,
                    x: cell.mxGeometry?.x,
                    y: cell.mxGeometry?.y,
                    cellDefaultName: copiedCell.cellDefaultName,
                    value: copiedCell.value,
                    methodLocked: copiedCell.value.methodLocked,
                    flowItemsLocked: copiedCell.value.flowItemsLocked,
                    ready: copiedCell.value.ready,
                    initialized: copiedCell.value.initialized,
                    metaType: cell.meta.metaType || cell.meta.style,
                });

                oldCellNewCellMapping[cell.id] = newCell.id;

                return newCell;
            }));

            const otherSequencesEdges = otherSequenceChildren.filter((child) => child.type === EDGE_TYPE);

            await Promise.all(otherSequencesEdges.map((cell) => {
                return this.addCellToDraft({
                    cellName: cell.type,
                    parent: selectedSequence,
                    source: oldCellNewCellMapping[cell.meta.source],
                    target: oldCellNewCellMapping[cell.meta.target],
                    metaType: cell.meta.metaType || cell.meta.style,
                });
            }));

            const currentSequenceChildrenAfter = findChildren(selectedSequence, this.draftJson?.cells);

            const startSequences = currentSequenceChildrenAfter.filter((cell) => cell.type === SEQUENCE_START);

            if (startSequences.length === 0) {
                await this.addCellToDraft({
                    cellName: SEQUENCE_START,
                    cellDefaultName: this.$t('funnel.edit.sideBar.flow.start'),
                    parent: selectedSequence,
                    x: GRID_SIZE,
                    y: GRID_SIZE,
                });
            }

            this.markDirty();
            await this.saveCampaign();
        },

        setAddContactToAutomation(addContacts) {
            this.addContactToAutomation = addContacts;
            this.validateAndPublishKonvaCampaign();
            this.closeAddContactToAutomation();
        },

        updateDismissAddContacts(dismissModal) {
            this.dismissAddContactsModal = dismissModal;
        },

        closeAddContactToAutomation() {
            this.showAddContactModal = false;
            clearPotentialContactsList(this.funnelInfo.funnelId);
        },

        closePublishSuccessModal() {
            this.showPublishSuccess = false;
        },

        async toggleCampaignLock(locked) {
            try {
                await this.saveCampaign();

                await updateLockForFunnel(this.funnelInfo.funnelId, locked);
                this.$toast({
                    message: this.funnelInfo.locked
                        ? this.$t('funnel.unlock.notification')
                        : this.$t('funnel.lock.notification'),
                    action: () => {
                        this.toggleCampaignLock(!this.funnelInfo.locked);
                    },
                    actionText: this.$t('funnel.lockUnlock.action'),
                });

                this.funnelInfo.locked = !this.funnelInfo.locked;
                this.toggleReportingView();
            } catch (error) {
                this.$toast({ message: this.$t('funnel.lock.failure') });
            }
        },

        async unpublishCampaign() {
            try {
                await unpublish(this.funnelInfo.funnelId, this.$t('funnel.processing.cancelled.unpublished.message.default'));

                this.markDirty();

                this.draftJson?.cells.forEach((cell) => {
                    cell.meta.published = '0';
                });

                await this.saveCampaign();

                this.funnelInfo.published = false;

                const message = this.$t('funnel.edit.unpublish.finalized');

                this.$toast({ message });
            } catch (error) {
                this.$toast({ message: this.$t('funnel.edit.unpublish.failure'), type: 'error' });
                // eslint-disable-next-line no-console
                console.error('Error:', error);
            }
        },

        async onShowPublishModal(isOpen) {
            await this.saveCampaign();
            this.showPublish = isOpen;
        },

        async validateAndPublishKonvaCampaign() {
            try {
                this.saveStatus = SAVE_STATUS.SAVING;
                const draftJsonCells = [...(this.draftJson?.cells || [])];
                const cellsToPublish = [];
                const newOrModifiedCells = [];

                draftJsonCells.forEach((cell) => {
                    const isPublished = cell.value?.published === '1';
                    const isEdge = cell.type === EDGE_TYPE;
                    let isReady;

                    if (isEdge) {
                        const sourceCell = draftJsonCells.find((draftCell) => draftCell.meta.id === cell.meta.source);
                        const targetCell = draftJsonCells.find((draftCell) => draftCell.meta.id === cell.meta.target);
                        const sourceCellIsReady = sourceCell?.value?.ready === '1';
                        const targetCellIsReady = targetCell?.value?.ready === '1';

                        isReady = (sourceCellIsReady && targetCellIsReady);
                    } else {
                        isReady = cell.value.ready === '1';
                    }

                    if (cell.meta.parent !== '1') {
                        // don't publish elements marked as ready, but contained in sequences not marked as ready
                        const parentCell = draftJsonCells.find((draftCell) => draftCell.meta.id === cell.meta.parent);

                        isReady = isReady && parentCell?.value?.ready === '1';
                    }

                    if (isReady) {
                        cellsToPublish.push(cell);
                        if (cell.type === EDGE_TYPE) return;

                        cell.value.published = '1';
                        if (!isPublished) newOrModifiedCells.push(cell);
                    } else if (isPublished) {
                        if (cell.type === EDGE_TYPE) return;

                        newOrModifiedCells.push(cell); // "unpublish" is a modification
                    }
                });

                const sanitizedCells = this.sanitizeBeforePublish(cellsToPublish);

                const jsonToPublish = { cells: [draftJsonCells[0], draftJsonCells[1], ...sanitizedCells] };

                const xmlToPublish = mapCampaignJsonToXml(jsonToPublish);

                this.updateCells(draftJsonCells);
                await this.saveCampaign();

                const draftXml = mapCampaignJsonToXml(this.draftJson);

                await axios.put(`/app/funnel/${this.funnelInfo.funnelId}/publish`, {
                    newOrModifiedVertexIds: newOrModifiedCells.map((cell) => cell.meta.id),
                    xml: xmlToPublish,
                    addContactsOnPublish: this.addContactToAutomation,
                    useReportToFilterContacts: false,
                    dismissAddContactsModal: false,
                });

                this.funnelInfo.published = true;

                this.publishedJson = jsonToPublish;
                this.draftXml = draftXml;
                this.funnelInfo.publishedXml = xmlToPublish;

                this.showPublish = false;
                this.saveStatus = SAVE_STATUS.SUCCESS;
                this.verifySuccessPublish();
                this.$toast({ message: this.$t('funnel.edit.publish.finalized') });
            } catch (err) {
                this.saveStatus = SAVE_STATUS.FAILED;
                this.$toast({ message: this.$t('funnel.edit.publish.failure'), type: 'error' });
            }
        },

        sanitizeBeforePublish(cellsToPublish) {
            const clonedCellsToPublish = clonedeep(cellsToPublish);
            const filteredDecisionCells = clonedCellsToPublish.filter((cell) => cell.type === DECISION_TYPE);

            return sanitizeDecisionBeforePublish(filteredDecisionCells, clonedCellsToPublish);
        },

        async verifyAndPublish(event) {
            const { addContacts } = event;

            this.showPublish = false;

            if (addContacts && !this.dismissAddContactsModal) {
                await this.saveCampaign();
                this.showAddContactModal = true;
            } else {
                await this.validateAndPublishKonvaCampaign();
            }
        },

        verifySuccessPublish() {
            if (this.funnelInfo.published) {
                this.showPublishSuccess = true;
            }
        },

        async updateCellName({ cell }) {
            try {
                await syncLabel(cell);
                this.updateCellValue({ cell, preventClose: true, isReady: cell.value.ready === '1' });
            } catch (error) {
                this.$toast({ message: this.$t('funnel.edit.cell.name.failure'), type: 'error' });
            }
        },

        updateCellValue({
            cell, preventClose = false, isReady = true,
        }) {
            const [...updatedCells] = this.draftJson?.cells || [];
            const cellIndex = updatedCells.findIndex((existingCell) => existingCell.meta.id === cell.meta.id);

            this.brokenCellsById = {};
            cell.value.published = '0';
            cell.value.initialized = '1';
            cell.value.ready = isReady ? '1' : '0';
            // If the cell type is 'automatedSms', add the automationMessageId

            if (cell.type === 'automatedSms') {
                cell.value.automationMessageId = this.automationMessageId;
            }

            const connectedCells = [...findIncomingCells(cell.id, updatedCells), ...findOutgoingCells(cell.id, updatedCells)];

            connectedCells.forEach((connectedCell) => {
                if (connectedCell.type === DECISION_TYPE) {
                    const decisionIncoming = findIncomingCells(connectedCell.id, updatedCells);
                    const decisionOutgoing = findOutgoingCells(connectedCell.id, updatedCells);

                    const incomingIsReady = decisionIncoming.find((c) => c.value.ready === '1');
                    const hasTwoOutcomingReady = decisionOutgoing.filter((c) => c.value.ready === '1')?.length > 1;

                    const decisionCellIndex = updatedCells.findIndex((existingCell) => existingCell.meta.id === connectedCell.meta.id);

                    connectedCell.value.published = '0';
                    connectedCell.value.ready = incomingIsReady && hasTwoOutcomingReady ? '1' : '0';

                    updatedCells[decisionCellIndex] = connectedCell;
                }
            });

            updatedCells[cellIndex] = cell;
            this.draftJson.cells = updatedCells;
            this.markDirty();

            if (cell.value.broken === '1') {
                this.checkForBrokenCells();
            }

            if (!preventClose) {
                this.closeModal();
            }
        },

        async checkForBrokenCells() {
            const brokenCells = await checkBrokenCells([...(this.draftJson?.cells || [])]);

            brokenCells.forEach((cell) => {
                this.brokenCellsById[cell.id] = cell;
            });

            let updatedBrokenStatus = false;

            const cellsWithUpdatedBrokenStatus = this.draftJson?.cells.map((cell) => {
                const updatedCell = { ...cell };

                if (this.brokenCellsById[cell.meta.id]) {
                    updatedCell.value.broken = '1';
                    updatedCell.value.ready = '0';
                    updatedBrokenStatus = true;
                } else if (cell.value.broken === '1') {
                    updatedCell.value.broken = '0';
                    updatedBrokenStatus = true;
                }

                return updatedCell;
            });

            if (updatedBrokenStatus) this.updateCells(cellsWithUpdatedBrokenStatus);
        },

        async checkForTimeZones() {
            const { numTimersUpdated, oldTimeZoneId, newTimeZoneId } = this.builderInfo;

            if (numTimersUpdated > 0) {
                const response = await getLabelsFromTimeZoneChanged(oldTimeZoneId, newTimeZoneId);

                const {
                    oldTimeZoneLabel,
                    newTimeZoneLabel,
                    oldDateTime,
                    newDateTime,
                } = response;

                this.oldTimeZoneLabel = oldTimeZoneLabel;
                this.newTimeZoneLabel = newTimeZoneLabel;
                this.oldDateTime = oldDateTime;
                this.newDateTime = newDateTime;

                this.showTimeZoneChangedModal = true;
            }
        },

        closeTimeZoneChangedModal() {
            this.showTimeZoneChangedModal = false;
        },

        openCellModalById({ cellId }) {
            const cell = this.draftJson?.cells.find((draftCell) => draftCell.meta.id === cellId);

            this.showPublish = false;

            if (cell.type === FLOW_TYPE) {
                this.onSetSequenceId(cellId);
            } else {
                this.openCellModal(cell);
            }
        },

        refreshPublishInfoIfOpen() {
            if (this.showPublish) {
                this.saveCampaign();
            }
        },

        async updateCategories(updatedCategories) {
            this.allCategories = (await getAllCategories()).map(({ label, value }) => {
                return { label, value };
            });

            this.assignedCategories = this.allCategories.filter((category) => {
                return updatedCategories.includes(category.value);
            });
        },

        async fetchCategories() {
            try {
                this.allCategories = (await getAllCategories()).map(({ label, value }) => {
                    return { label, value };
                });

                const categoriesAssigned = (await getAllCategoriesAssigned(this.funnelInfo.funnelId)).map((categoryId) => {
                    return categoryId;
                });

                this.assignedCategories = this.allCategories.filter((category) => {
                    return categoriesAssigned.includes(category.value);
                });
                this.categoriesLoaded = true;
            } catch (error) {
                this.$toast({ message: this.$t('funnel.categories.loading.error'), type: 'error' });
            }
        },

        async fetchMergeFields() {
            this.loadingMergeFields = true;

            try {
                this.mergeFields = await getMergeFieldsDropdown(this.funnelInfo.funnelId);
            } catch (error) {
                this.mergeFields = [];
                // eslint-disable-next-line no-console
                console.error('Error:', error);
            }

            this.loadingMergeFields = false;
        },

        async fetchItemDisplayConfiguration() {
            try {
                const config = await getConfig(this.funnelInfo.funnelId);
                const hasConfig = config.opensAutomatically !== undefined;

                this.autoOpenConfigScreen = hasConfig ? !!config.opensAutomatically : true;
            } catch (e) {
                this.autoOpenConfigScreen = true;
            }
        },

        updateOpenAutomaticallyCampaign(isAutomatically) {
            this.autoOpenConfigScreen = isAutomatically;
        },

        async getEnabledCellTypes() {
            this.enabledCellTypes = await getEnabledCellTypes();
            this.enabledIntegrations = await getEnabledIntegrations();
        },

        async importXmlToBuilder(event) {
            await this.loadXmlToBuilder(event, 'funnel.edit.import.success', 'funnel.edit.import.error');
        },

        async loadXmlToBuilder(event, messageLLabel, errorLabel) {
            const {
                xmlToImport,
            } = event;

            try {
                const draftJson = await mapCampaignXmlToJson(xmlToImport);

                this.draftXml = xmlToImport;
                this.draftJson = draftJson;

                this.$toast({ message: this.$t(messageLLabel) });
            } catch (error) {
                this.$toast({ message: this.$t(errorLabel), type: 'error' });
            }
        },

        async restoreVersion(event) {
            const {
                revisionId,
            } = event;
            const revisionXml = await restoreVersion(revisionId, this.draftXml, this.publishedXml);

            await this.loadXmlToBuilder({ xmlToImport: revisionXml }, 'funnel.edit.restore.success', 'funnel.edit.restore.error');
        },

        async revertChanges() {
            try {
                await this.saveCampaign();
                await revertXml(this.funnelInfo.draftXml, this.draftXml, this.funnelInfo.publishedXml);

                const revertedJson = await mapCampaignXmlToJson(this.funnelInfo.draftXml);
                const { hasChanges, updatedCells } = revertAdditionalObjectsIfNeeded(this.draftJson, revertedJson);

                this.draftXml = this.funnelInfo.draftXml;

                if (hasChanges) {
                    this.draftJson = { ...this.draftJson, cells: updatedCells };
                    this.markDirty();
                } else {
                    this.draftJson = revertedJson;
                }

                this.$toast({ message: this.$t('funnel.edit.revert.success') });
            } catch (e) {
                this.$toast({ message: this.$t('funnel.edit.revert.error'), type: 'error' });
            }
        },

        openFeedbackModal() {
            this.isFeedbackModalOpen = true;
        },

        openNewEmailExperienceModal(cell) {
            this.automaticallyConvertBard = true;
            this.openCellModal(cell);
        },

        async fetchAutomationMessageId() {
            try {
                const { data } = await axios.post('/app/automatedSmsFunnel', {
                    funnelId: this.funnelId,
                });

                this.automationMessageId = data.automationMessageId;
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Failed to fetch automationMessageId:', error);
            }
        },

        async toggleBetaFeatures() {
            try {
                const swap = this.betaFeaturesEnabled.toString(); // passing value before toggling, endpoint assumes negated logic (disable)!

                await disableBetaFeatures(swap);

                this.appInfo.betaFeaturesEnabled = !this.appInfo.betaFeaturesEnabled;
            } catch (error) {
                this.$error({ message: `Failed to toggle automation builder beta features: ${error}` });
            }
        },

        handleMigratedBardToUnlayer(data) {
            this.updateCellValue(data);
            this.autoOpenContentAfterMigration = !!data.cell.value.marketingEmailId;
            this.openCellModal(data.cell);
        },
    },
};

</script>

<style lang="scss" scoped>
.konva-campaign-builder-container :deep(button.loading)  {
    /* styles from sink_css.jsp are changing this */
    display: initial !important;
}

.konva-campaign-builder-container {
    width: 100vw;
    height: 100vh;
    overflow: hidden;
}

.campaign-builder-container {
    width: 100vw;
    height: 100vh;
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: px-to-rem(70px) auto;
    overflow: hidden;
}
.campaign-builder-body {
    align-items: flex-start;
    border-bottom: 1px solid $color-gray-400;
    display: flex;
    height: calc(100vh - 3.5rem);
    justify-content: flex-start;
    width: 100%;
    &.hidden {
        display: none;
    }
}
.campaign-header-container {
    border-bottom: 1px solid $color-gray-200;
    background: $color-paper;
    height: px-to-rem(72px);
}

#keyboardShortcuts.fade-out {
    width: 0px;
    border: none;
    animation-name: slideInLeft;
    transition: all 0.5s ease-out;
    animation-duration: 0.5s;
    animation-fill-mode: both;
}

.vue-funnel-header-shrink {
    padding-right: px-to-rem(320px);
    animation-name: shrink;
    transition: all .5s ease-out;
    animation-duration: 1s;
    animation-fill-mode: both;
}

.timezone-info {
    margin-bottom: px-to-rem(25px);
    width: 100%;
}

.timezone-desc {
    margin-bottom: px-to-rem(30px);
}
</style>
<style lang="scss">
.page_funnelFunnelEditor #footer {
    position: absolute;
    width: 100%;
    bottom: 0;
    margin-bottom: $spacing-150;
}
.campaign-builder-container .campaign-builder-body .mxRubberband {
    position: absolute;
    border: 1px solid $color-blue-600;
    background-color: $color-blue-600;
}
</style>
